/**
 * Syncs any input/textarea with a `data-cart-attribute` attribute to the
 * Shopify cart via /cart/update.js when its value changes. The attribute name
 * sent to Shopify is taken from the element's `name` (expected form:
 * `attributes[Some Key]`). Updates are debounced per element so we don't fire
 * a request on every keystroke.
 *
 * Markup contract:
 *   <textarea name="attributes[Gift message]" data-cart-attribute>...</textarea>
 *
 * Storefront snippets that include this script don't need any JS of their own.
 */
(function () {
  'use strict';

  /** @type {Map<HTMLElement, number>} */
  const timers = new Map();
  const DEBOUNCE_MS = 400;

  /**
   * Extracts the attribute key from a `name` like `attributes[Gift message]`.
   * @param {string} name
   * @returns {string|null}
   */
  function parseAttributeKey(name) {
    const match = /^attributes\[(.+)\]$/.exec(name || '');
    return match ? match[1] : null;
  }

  /**
   * Pushes a single cart attribute change to Shopify.
   * @param {string} key
   * @param {string} value
   */
  function pushAttribute(key, value) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [key]: value } }),
    }).catch(() => {
      // Silently ignore: the next change attempt will retry, and the value
      // remains in the input so the customer hasn't lost their text.
    });
  }

  function handleChange(event) {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
    if (!el.hasAttribute('data-cart-attribute')) return;

    const key = parseAttributeKey(el.name);
    if (!key) return;

    const existing = timers.get(el);
    if (existing) clearTimeout(existing);

    const timer = window.setTimeout(() => {
      timers.delete(el);
      pushAttribute(key, el.value);
    }, DEBOUNCE_MS);
    timers.set(el, timer);
  }

  document.addEventListener('input', handleChange, true);
  document.addEventListener('change', handleChange, true);
})();
